/**
 * unknownEvents — makes an unrecognised wire event DISCOVERABLE.
 *
 * This exists because of a concrete, expensive failure: the previous
 * `transport/eventSchema.ts` was a closed zod union over the old wire vocabulary, so
 * when the backend switched to `io.doclm.*` it rejected **100% of frames** and dropped
 * them with no log anywhere. Streaming was non-functional and nothing in the app
 * said so.
 */

interface UnknownEventRecord {
  eventType: string;
  domain: string;
  phaseRaw: string;
  count: number;
  firstSeenAt: number;
  lastSeenAt: number;
}

const records = new Map<string, UnknownEventRecord>();

function isDev(): boolean {
  return Boolean(import.meta.env?.DEV);
}

/**
 * Record an `io.doclm.*` event whose domain has no registered interpreter.
 */
export function noteUnknownEvent(args: {
  eventType: string;
  domain: string;
  phaseRaw: string;
}): number {
  const now = Date.now();
  const existing = records.get(args.eventType);

  if (existing) {
    existing.count += 1;
    existing.lastSeenAt = now;
    return existing.count;
  }

  records.set(args.eventType, {
    eventType: args.eventType,
    domain: args.domain,
    phaseRaw: args.phaseRaw,
    count: 1,
    firstSeenAt: now,
    lastSeenAt: now
  });

  if (isDev()) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ChatV2/steps] No interpreter for "${args.eventType}" (domain "${args.domain}", phase "${args.phaseRaw}"). NO step was created. Register a domain interpreter in steps/domains/ if this should be modelled.`
    );
  }

  return 1;
}

/** Snapshot for diagnostics / the transport health surface. */
export function getUnknownEvents(): UnknownEventRecord[] {
  return [...records.values()].sort((a, b) => b.count - a.count);
}

/** Total unrecognised `io.doclm.*` frames seen this session. */
export function getUnknownEventCount(): number {
  let total = 0;
  for (const record of records.values()) {
    total += record.count;
  }
  return total;
}

/** Test teardown / HMR. */
export function resetUnknownEvents(): void {
  records.clear();
}
